import { useId, useMemo, useState } from "react";

/**
 * Charts, drawn as inline SVG.
 *
 * Conventions kept deliberately uniform across both charts:
 *   • a series keeps its colour slot regardless of what else is on screen;
 *   • marks are capped at 24px and separated by a 2px gap in the surface
 *     colour rather than by a stroke;
 *   • gridlines are hairline and recessive, labels wear text tokens — never
 *     the series colour — so identity always comes from a swatch beside the
 *     text, not the text itself.
 */

const SLOTS = [1, 2, 3, 4, 5, 6, 7, 8].map((n) => `var(--viz-${n})`);
export const slotColor = (i) => SLOTS[i % SLOTS.length];

const money = (n, currency = "USD") =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    maximumFractionDigits: Math.abs(n) >= 1000 ? 0 : 2,
  }).format(n || 0);

/** Round an axis maximum up to a clean 1/2/5 × 10ⁿ step. */
function niceScale(max, ticks = 4) {
  if (!max || max <= 0) return { max: 1, step: 1 };
  const raw = max / ticks;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / mag;
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10) * mag;
  return { max: Math.ceil(max / step) * step, step };
}

function Legend({ items }) {
  return (
    <div className="viz-legend">
      {items.map((it) => (
        <span key={it.label}>
          <i style={{ background: it.color }} />
          <span className="viz-legend-label">{it.label}</span>
          {it.value != null && <b>{it.value}</b>}
        </span>
      ))}
    </div>
  );
}

/**
 * Grouped columns: income beside expense, one pair per period.
 * Two series, so a legend is always shown; only the tallest column in view is
 * direct-labelled — a number on every bar reads as noise.
 */
export function IncomeExpenseChart({ buckets, currency = "USD", height = 240 }) {
  const gid = useId();
  const [hover, setHover] = useState(null);

  const { max, step } = useMemo(
    () => niceScale(Math.max(0, ...buckets.flatMap((b) => [b.income, b.expense]))),
    [buckets]
  );

  if (!buckets.length) return null;

  const padL = 62, padR = 12, padT = 14, padB = 34;
  const width = Math.max(320, buckets.length * 74 + padL + padR);
  const plotW = width - padL - padR;
  const plotH = height - padT - padB;

  const band = plotW / buckets.length;
  const barW = Math.min(24, (band - 10) / 2);
  const y = (v) => padT + plotH - (v / max) * plotH;

  const tickValues = Array.from({ length: Math.round(max / step) + 1 }, (_, i) => i * step);
  const peak = Math.max(...buckets.flatMap((b) => [b.income, b.expense]));

  return (
    <div className="viz">
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height}
        role="img" aria-label="Income and expenses by period">
        {/* gridlines + y ticks */}
        {tickValues.map((v) => (
          <g key={v}>
            <line x1={padL} x2={width - padR} y1={y(v)} y2={y(v)}
              stroke="var(--viz-grid)" strokeWidth="1" />
            <text x={padL - 8} y={y(v) + 4} textAnchor="end" className="viz-tick">
              {v >= 1000 ? `${Math.round(v / 1000)}k` : Math.round(v)}
            </text>
          </g>
        ))}

        {buckets.map((b, i) => {
          const x0 = padL + i * band;
          // The 2px gap between the pair is surface colour, not a stroke.
          const gap = 2;
          const cx = x0 + band / 2;
          const bars = [
            { key: "income", v: b.income, color: slotColor(0), x: cx - barW - gap / 2 },
            { key: "expense", v: b.expense, color: slotColor(1), x: cx + gap / 2 },
          ];

          return (
            <g key={b.key}>
              {bars.map((bar) => {
                const h = Math.max(0, plotH - (y(bar.v) - padT));
                const isPeak = bar.v === peak && peak > 0;
                return (
                  <g key={bar.key}
                    onMouseEnter={() => setHover({ ...b, focus: bar.key })}
                    onMouseLeave={() => setHover(null)}>
                    {/* generous hit target, invisible */}
                    <rect x={bar.x - 3} y={padT} width={barW + 6} height={plotH} fill="transparent" />
                    <path d={roundedTop(bar.x, y(bar.v), barW, h, 4)} fill={bar.color}
                      opacity={hover && hover.key !== b.key ? 0.45 : 1} />
                    {isPeak && (
                      <text x={bar.x + barW / 2} y={y(bar.v) - 6} textAnchor="middle" className="viz-value">
                        {money(bar.v, currency)}
                      </text>
                    )}
                  </g>
                );
              })}
              <text x={cx} y={height - 12} textAnchor="middle" className="viz-tick">
                {compactLabel(b.label)}
              </text>
            </g>
          );
        })}

        {/* baseline sits on top of the gridline stack */}
        <line x1={padL} x2={width - padR} y1={y(0)} y2={y(0)}
          stroke="var(--border-strong)" strokeWidth="1" />
      </svg>

      <Legend items={[
        { label: "Income", color: slotColor(0) },
        { label: "Expenses", color: slotColor(1) },
      ]} />

      {hover && (
        <div className="viz-tooltip" key={`${gid}-${hover.key}`}>
          <strong>{hover.label}</strong>
          <span><i style={{ background: slotColor(0) }} /> Income <b>{money(hover.income, currency)}</b></span>
          <span><i style={{ background: slotColor(1) }} /> Expenses <b>{money(hover.expense, currency)}</b></span>
          <span className="viz-net">Net <b>{money(hover.income - hover.expense, currency)}</b></span>
        </div>
      )}
    </div>
  );
}

/**
 * Axis labels come from the period formatter as full prose ("April 2026").
 * Abbreviate the month rather than clipping the string, so a tick never ends
 * in an ellipsis.
 */
const MONTH_ABBR = /\b(January|February|March|April|May|June|July|August|September|October|November|December)\b/g;
function compactLabel(label) {
  const short = String(label).replace(MONTH_ABBR, (m) => m.slice(0, 3));
  return short.length > 14 ? `${short.slice(0, 13)}…` : short;
}

/** Column path with a 4px rounded cap and square corners at the baseline. */
function roundedTop(x, yTop, w, h, r) {
  if (h <= 0) return "";
  const rad = Math.min(r, h, w / 2);
  return [
    `M${x},${yTop + h}`,
    `V${yTop + rad}`,
    `Q${x},${yTop} ${x + rad},${yTop}`,
    `H${x + w - rad}`,
    `Q${x + w},${yTop} ${x + w},${yTop + rad}`,
    `V${yTop + h}`,
    "Z",
  ].join(" ");
}

/**
 * Category breakdown. Slices past the eighth fold into "Other" rather than
 * inventing a ninth hue, and every slice is named with its value in the legend
 * so identity never rests on colour alone.
 */
export function CategoryDonut({ data, title, currency = "USD", size = 190 }) {
  const [hover, setHover] = useState(null);

  const slices = useMemo(() => {
    const sorted = [...data].sort((a, b) => b.amount - a.amount);
    if (sorted.length <= 8) return sorted;
    const head = sorted.slice(0, 7);
    const rest = sorted.slice(7).reduce((sum, d) => sum + d.amount, 0);
    return [...head, { category: "Other", amount: rest }];
  }, [data]);

  const total = slices.reduce((s, d) => s + d.amount, 0);
  if (!total) return null;

  const r = size / 2;
  const thickness = 26;
  const inner = r - thickness;
  // 2px of surface between slices, expressed as an angle at the mid-radius.
  const gapAngle = (2 / (r - thickness / 2)) * 0.5;

  let angle = -Math.PI / 2;
  const arcs = slices.map((d, i) => {
    const sweep = (d.amount / total) * Math.PI * 2;
    const a0 = angle + gapAngle;
    const a1 = angle + sweep - gapAngle;
    angle += sweep;
    return { ...d, a0: Math.min(a0, a1), a1: Math.max(a0, a1), color: slotColor(i) };
  });

  const active = hover != null ? arcs[hover] : null;

  return (
    <div className="viz viz-donut">
      {title && <div className="panel-title">{title}</div>}

      <div className="viz-donut-body">
        <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}
          role="img" aria-label={title || "Category breakdown"}>
          <g transform={`translate(${r},${r})`}>
            {arcs.map((a, i) => (
              <path key={a.category} d={arcPath(a.a0, a.a1, inner, r)} fill={a.color}
                opacity={hover != null && hover !== i ? 0.4 : 1}
                onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />
            ))}
            <text textAnchor="middle" y={-2} className="viz-center-label">
              {active ? active.category : "Total"}
            </text>
            <text textAnchor="middle" y={18} className="viz-center-value">
              {money(active ? active.amount : total, currency)}
            </text>
          </g>
        </svg>

        <Legend items={arcs.map((a) => ({
          label: a.category,
          color: a.color,
          value: money(a.amount, currency),
        }))} />
      </div>
    </div>
  );
}

function arcPath(a0, a1, rInner, rOuter) {
  const large = a1 - a0 > Math.PI ? 1 : 0;
  const p = (radius, angle) => [
    (radius * Math.cos(angle)).toFixed(2),
    (radius * Math.sin(angle)).toFixed(2),
  ];
  const [x0, y0] = p(rOuter, a0);
  const [x1, y1] = p(rOuter, a1);
  const [x2, y2] = p(rInner, a1);
  const [x3, y3] = p(rInner, a0);
  return `M${x0},${y0} A${rOuter},${rOuter} 0 ${large} 1 ${x1},${y1} L${x2},${y2} A${rInner},${rInner} 0 ${large} 0 ${x3},${y3} Z`;
}
